/**
 * Worksheet API layer. The Django API wraps every body in the standard
 * envelope (`{ success, message, data }`, plus `meta` for lists); `apiFetch`
 * returns it verbatim, so this layer unwraps `data` and maps snake_case↔camelCase.
 *
 * Money is integer **cents** on both sides — no conversion here; the form edge
 * converts to/from dollar strings. Reads deep-camelize; writes deep-decamelize.
 */

import { apiFetch, apiFetchBlob } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import type {
  Allowances,
  FinancialAccount,
  IncomeExpense,
  InsurancePolicy,
  Lawsuit,
  OfferSummary,
  OtherAsset,
  OtherBusiness,
  Property,
  TaxDebt,
  Vehicle,
  Worksheet,
  AssetTransfer,
} from "@/features/worksheets/types";

interface Envelope<T> {
  data: T;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const toCamel = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
const toSnake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

/** Deep key-rename; array/scalar values pass through unchanged. */
function rename<T>(value: unknown, fn: (k: string) => string): T {
  if (Array.isArray(value)) return value.map((v) => rename(v, fn)) as T;
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[fn(k)] = rename(v, fn);
    return out as T;
  }
  return value as T;
}

const camelize = <T>(v: unknown): T => rename<T>(v, toCamel);
const decamelize = (v: unknown): unknown => rename(v, toSnake);

const base = (caseId: string) => `/cases/${caseId}/worksheet`;

// --- header ------------------------------------------------------------------

export async function getWorksheet(caseId: string): Promise<Worksheet> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}/`);
  return camelize<Worksheet>(res.data);
}

/** Autosave patch: keys are camelCase, converted to snake_case here. */
export async function patchWorksheet(
  caseId: string,
  patch: Record<string, unknown>,
): Promise<Worksheet> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}/`, {
    method: "PATCH",
    json: decamelize(patch),
  });
  return camelize<Worksheet>(res.data);
}

// --- income / expense --------------------------------------------------------

export async function getIncomeExpense(caseId: string): Promise<IncomeExpense> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}/incexp/`);
  return camelize<IncomeExpense>(res.data);
}

export async function patchIncomeExpense(
  caseId: string,
  patch: Record<string, unknown>,
): Promise<IncomeExpense> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}/incexp/`, {
    method: "PATCH",
    json: decamelize(patch),
  });
  return camelize<IncomeExpense>(res.data);
}

// --- computed summaries ------------------------------------------------------

export async function getOfferSummary(caseId: string): Promise<OfferSummary> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}/offer/`);
  return camelize<OfferSummary>(res.data);
}

export async function getAllowances(caseId: string): Promise<Allowances> {
  const res = await apiFetch<Envelope<unknown>>(`${base(caseId)}/allowances/`);
  return camelize<Allowances>(res.data);
}

export type ExportFormat = "pdf" | "xlsx";

export async function downloadPaymentSchedule(caseId: string, fmt: ExportFormat): Promise<void> {
  const blob = await apiFetchBlob(`${base(caseId)}/offer/schedule/?format=${fmt}`);
  downloadBlob(`payment-schedule.${fmt}`, blob);
}

export async function downloadIaWorksheet(caseId: string, fmt: ExportFormat): Promise<void> {
  const blob = await apiFetchBlob(`${base(caseId)}/offer/ia-worksheet/?format=${fmt}`);
  downloadBlob(`ia-worksheet.${fmt}`, blob);
}

// --- generic nested collections ---------------------------------------------

export interface CollectionApi<T> {
  list: (caseId: string) => Promise<T[]>;
  create: (caseId: string, payload: Record<string, unknown>) => Promise<T>;
  update: (caseId: string, id: string, payload: Record<string, unknown>) => Promise<T>;
  remove: (caseId: string, id: string) => Promise<void>;
}

function collection<T>(segment: string): CollectionApi<T> {
  const url = (caseId: string) => `${base(caseId)}/${segment}/`;
  return {
    async list(caseId) {
      const res = await apiFetch<Envelope<unknown[]>>(url(caseId));
      return res.data.map((row) => camelize<T>(row));
    },
    async create(caseId, payload) {
      const res = await apiFetch<Envelope<unknown>>(url(caseId), {
        method: "POST",
        json: decamelize(payload),
      });
      return camelize<T>(res.data);
    },
    async update(caseId, id, payload) {
      const res = await apiFetch<Envelope<unknown>>(`${url(caseId)}${id}/`, {
        method: "PATCH",
        json: decamelize(payload),
      });
      return camelize<T>(res.data);
    },
    async remove(caseId, id) {
      await apiFetch<void>(`${url(caseId)}${id}/`, { method: "DELETE" });
    },
  };
}

export const taxDebtsApi = collection<TaxDebt>("tax-debts");
export const propertiesApi = collection<Property>("properties");
export const vehiclesApi = collection<Vehicle>("vehicles");
export const otherAssetsApi = collection<OtherAsset>("assets");
export const accountsApi = collection<FinancialAccount>("accounts");
export const insuranceApi = collection<InsurancePolicy>("insurance");
export const transfersApi = collection<AssetTransfer>("transfers");
export const lawsuitsApi = collection<Lawsuit>("lawsuits");
export const otherBusinessesApi = collection<OtherBusiness>("other-businesses");

/** Accounts filtered by class (banking sub-sections). */
export async function listAccountsByClass(
  caseId: string,
  accountClass: string,
): Promise<FinancialAccount[]> {
  const res = await apiFetch<Envelope<unknown[]>>(
    `${base(caseId)}/accounts/?class=${accountClass}`,
  );
  return res.data.map((row) => camelize<FinancialAccount>(row));
}

// --- reused Client-hub records (Personal & Employment tab) -------------------
// The worksheet's Personal tab reads/writes the Client's own dependents and
// employers (+ the new wage/SSN/paystub fields) rather than re-collecting them.

const SUBRECORD_PAGE = "?page_size=100";

async function listRecords<T>(path: string): Promise<T[]> {
  const res = await apiFetch<Envelope<unknown[]>>(`${path}${SUBRECORD_PAGE}`);
  return res.data.map((row) => camelize<T>(row));
}

async function createRecord<T>(path: string, payload: Record<string, unknown>): Promise<T> {
  const res = await apiFetch<Envelope<unknown>>(path, { method: "POST", json: decamelize(payload) });
  return camelize<T>(res.data);
}

async function updateRecord<T>(path: string, payload: Record<string, unknown>): Promise<T> {
  const res = await apiFetch<Envelope<unknown>>(path, { method: "PATCH", json: decamelize(payload) });
  return camelize<T>(res.data);
}

const removeRecord = (path: string) => apiFetch<void>(path, { method: "DELETE" });

/** PATCH just the marital fields on the client (reused by the Personal tab —
 * the worksheet doesn't duplicate marital status). Keys are camelCase. */
export async function patchClientMarital(
  clientId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await apiFetch<Envelope<unknown>>(`/clients/${clientId}/`, {
    method: "PATCH",
    json: decamelize(patch),
  });
}

export const listDependents = <T>(clientId: string) =>
  listRecords<T>(`/clients/${clientId}/dependents/`);
export const createDependent = <T>(clientId: string, p: Record<string, unknown>) =>
  createRecord<T>(`/clients/${clientId}/dependents/`, p);
export const updateDependent = <T>(clientId: string, id: string, p: Record<string, unknown>) =>
  updateRecord<T>(`/clients/${clientId}/dependents/${id}/`, p);
export const deleteDependent = (clientId: string, id: string) =>
  removeRecord(`/clients/${clientId}/dependents/${id}/`);

export const listEmployers = <T>(clientId: string) =>
  listRecords<T>(`/clients/${clientId}/employers/`);
export const createEmployer = <T>(clientId: string, p: Record<string, unknown>) =>
  createRecord<T>(`/clients/${clientId}/employers/`, p);
export const updateEmployer = <T>(clientId: string, id: string, p: Record<string, unknown>) =>
  updateRecord<T>(`/clients/${clientId}/employers/${id}/`, p);
export const deleteEmployer = (clientId: string, id: string) =>
  removeRecord(`/clients/${clientId}/employers/${id}/`);

export const listPayStubs = <T>(clientId: string, employerId: string) =>
  listRecords<T>(`/clients/${clientId}/employers/${employerId}/paystubs/`);
export const createPayStub = <T>(clientId: string, employerId: string, p: Record<string, unknown>) =>
  createRecord<T>(`/clients/${clientId}/employers/${employerId}/paystubs/`, p);
export const updatePayStub = <T>(
  clientId: string,
  employerId: string,
  id: string,
  p: Record<string, unknown>,
) => updateRecord<T>(`/clients/${clientId}/employers/${employerId}/paystubs/${id}/`, p);
export const deletePayStub = (clientId: string, employerId: string, id: string) =>
  removeRecord(`/clients/${clientId}/employers/${employerId}/paystubs/${id}/`);
